/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CSV CELLS — the one place that knows a spreadsheet is not a screen.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── WHY THIS IS NOT `display-labels.ts` ──────────────────────────────────
 *
 * `fmtCurrency` and its siblings are the right answer for anything a person
 * READS: absent renders '—', money carries its symbol and its thousands
 * separators. Every one of those is wrong in a CSV cell.
 *
 *   '—'       a spreadsheet sums it as 0, or refuses the column as text
 *   '$1,234'  Excel imports as a string; the column stops being numeric
 *
 * So a CSV needs a different contract for the SAME rule, and the rule is the
 * one CLAUDE.md's first non-negotiable states: an absent value must not become
 * a zero. In this medium the honest spelling of absence is an EMPTY CELL.
 * Nothing sums it, nothing rounds it, and a reader opening the file sees a gap
 * where there is a gap — which is exactly what the em dash does on screen.
 *
 * A `0.00` here would be the export undoing what the screen got right. It is
 * also the harder half to notice: the screen can be read against the API, the
 * spreadsheet leaves the building.
 *
 * ── WHY IT IS A MODULE AND NOT TWO COPIES ────────────────────────────────
 *
 * Eleven screens in this repo build a CSV and every one of them hand-rolls the
 * quoting. `BillsOfLading.tsx` escapes `"` correctly and quotes conditionally;
 * `TimesheetReports.tsx` replaces `"` with `'`, which silently alters the
 * exported text. Two spellings of one rule is how one of them grows a bug
 * nobody finds, and this is the chokepoint CLAUDE.md asks for instead of
 * instance N+1. New CSV call sites import these; the existing ones are not
 * converted here, which would be a sweep rather than this change.
 */

/**
 * One cell, always quoted, with `"` doubled per RFC 4180.
 *
 * ALWAYS quoted rather than only-when-needed, deliberately. A conditional quote
 * is a second rule to get right — it has to know about commas, quotes,
 * newlines, and a leading or trailing space — and getting it wrong splits one
 * field into two columns and shifts every column after it. An unconditionally
 * quoted field is legal everywhere and cannot do that.
 *
 * An absent value is an EMPTY, UNQUOTED cell: `""` and a truly empty cell both
 * import as blank, but the unquoted form is what a numeric column needs (see
 * `csvMoney`), so the two agree rather than differing by field type.
 */
export function csvCell(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  return `"${String(v).replace(/"/g, '""')}"`;
}

/**
 * A money or numeric cell: a bare number to two places, and EMPTY for absent.
 *
 * Unquoted and unformatted so the column stays numeric on import. `NaN` and
 * `Infinity` are absent rather than printed, for the reason `fmtCurrency`
 * rejects them on screen: neither is a figure anyone stated.
 *
 * NOT `fmtCurrency` — see the head of this file. That helper's '—' sums as
 * zero in a spreadsheet, which is the one outcome this whole module exists to
 * prevent.
 */
export function csvMoney(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return (n === 0 ? 0 : n).toFixed(2);
}
